import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Code, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VibeCodingStudio() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [codeGenerations, setCodeGenerations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const ticketsRes = await axios.get(`${API}/tickets`);
      const ticketsWithDev = ticketsRes.data.filter(t => 
        ['in_development', 'in_testing', 'in_deployment', 'in_governance', 'completed'].includes(t.status)
      );
      
      setTickets(ticketsWithDev);

      // Fetch code generations for each ticket
      const codeData = {};
      for (const ticket of ticketsWithDev) {
        try {
          const codeRes = await axios.get(`${API}/code/${ticket.id}`);
          if (codeRes.data.length > 0) {
            codeData[ticket.id] = codeRes.data;
          }
        } catch (err) {
          // No code yet
        }
      }
      setCodeGenerations(codeData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="vibe-coding-studio">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Vibe Coding Studio</h1>
        <p className="text-muted-foreground mt-2">AI-powered collaborative coding environment</p>
      </div>

      {tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Code className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No code generations yet. Execute agent workflows to generate code.</p>
            <Button onClick={() => navigate('/tickets')}>Go to Tickets</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map((ticket) => {
            const codes = codeGenerations[ticket.id] || [];
            const activeCode = codes.find(c => c.is_active) || codes[0];
            
            return (
              <Card key={ticket.id} className="hover:shadow-lg transition-shadow" data-testid={`code-card-${ticket.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
                        {activeCode && (
                          <>
                            <Badge variant="outline" className="text-xs">{activeCode.language}</Badge>
                            <Badge className="text-xs">v{activeCode.version}</Badge>
                          </>
                        )}
                      </div>
                      <CardTitle className="text-xl">{ticket.title}</CardTitle>
                      {activeCode && (
                        <CardDescription className="mt-2">{activeCode.description}</CardDescription>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigate(`/vibe-studio/${ticket.id}`)}
                      data-testid={`open-code-btn-${ticket.id}`}
                    >
                      <ExternalLink className="h-5 w-5" />
                    </Button>
                  </div>
                </CardHeader>
                {activeCode && (
                  <CardContent>
                    <div className="bg-slate-900 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                      <pre className="whitespace-pre-wrap">{activeCode.code.slice(0, 500)}{activeCode.code.length > 500 ? '...' : ''}</pre>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <div className="text-sm text-muted-foreground">
                        {codes.length} version{codes.length !== 1 ? 's' : ''} • Last updated: {new Date(activeCode.updated_at).toLocaleString()}
                      </div>
                      <Button onClick={() => navigate(`/vibe-studio/${ticket.id}`)} data-testid={`edit-code-btn-${ticket.id}`}>
                        <Code className="mr-2 h-4 w-4" /> Open in Editor
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
